import { faCalendarDays, faClose } from "@fortawesome/free-solid-svg-icons"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import InputField from "../../../components/InputField/InputField"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { SimpleToggle } from "../../../components/SimpleToggle/SimpleToggle"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import type { TimeClockEmployeeSummary } from "../../../Objects/TimeClock/TimeClockEmployeeSummary"
import type { EmployeeWorkSchedule } from "../../../Objects/TimeClock/EmployeeWorkSchedule"
import type { TimeClockScheduleModelType } from "../../../Objects/TimeClock/TimeClockScheduleModelType"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import GetSpace from "../../../utils/space"

interface TimeClockSchedulePopupProps {
  employee: TimeClockEmployeeSummary
  scheduleModel: TimeClockScheduleModelType
  onClose: () => void
  onSave: (days: EmployeeWorkSchedule[]) => Promise<boolean>
}

const daySuffixes = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"]

const buildInitialDays = (employee: TimeClockEmployeeSummary): EmployeeWorkSchedule[] =>
  daySuffixes.map((_, day) => {
    const current = employee.schedules.find((schedule) => schedule.day === day)

    return (
      current ?? {
        day,
        isDayOff: true,
        startTime: null,
        endTime: null,
        expectedMinutes: 0,
      }
    )
  })

export const TimeClockSchedulePopup: React.FC<TimeClockSchedulePopupProps> = ({
  employee,
  scheduleModel,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation(["timeClock", "helpPopup"])

  const [days, setDays] = useState<EmployeeWorkSchedule[]>(() => buildInitialDays(employee))

  const [isSaving, setIsSaving] = useState(false)

  const updateDay = (day: number, changes: Partial<EmployeeWorkSchedule>) =>
    setDays((current) => current.map((item) => (item.day === day ? { ...item, ...changes } : item)))

  const handleSubmit = async () => {
    if (isSaving) return

    setIsSaving(true)

    try {
      await onSave(days)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("schedule.title", { name: employee.employeeName })}
              icon={faCalendarDays}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={`${defaultStyle.ButtonsActions} flex items-center gap-1`}>
                  <PopupHelpButton
                    variant="global"
                    helpKey="pages.time-clock-schedule"
                    title={t("helpPopup:global.timeClockSchedule.title")}
                    description={t("helpPopup:global.timeClockSchedule.description")}
                    bullets={t("helpPopup:global.timeClockSchedule.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockSchedulePopup.button.onClose"
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            {scheduleModel === "None" ? (
              <Animate type={AnimationTypes.slideUpIn} duration="1.6s">
                <p className="p-3 text-sm text-slate-600">{t("schedule.model_disabled")}</p>
              </Animate>
            ) : (
              <form
                className="flex flex-col gap-3 p-2"
                onSubmit={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  void handleSubmit()
                }}
              >
                {days.map((day, index) => (
                  <Animate key={day.day} type={AnimationTypes.slideUpIn} duration={`${1.6 + index * 0.1}s`}>
                    <div className="flex flex-col gap-2 py-1">
                      <SimpleToggle
                        title={`${t(`common.day_${daySuffixes[day.day]}`)} · ${t("schedule.day_off")}`}
                        value={day.isDayOff}
                        onChange={(value) => updateDay(day.day, { isDayOff: value })}
                        descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockSchedulePopup.toggle.dayOff"
                      />

                      {!day.isDayOff && (
                        <div className="flex flex-wrap gap-2">
                          {scheduleModel === "WeeklySchedule" && (
                            <>
                              <InputField
                                type="time"
                                value={day.startTime ?? ""}
                                placeholder={t("schedule.start_time") + " - Ex: 18:00"}
                                onChange={(event) => updateDay(day.day, { startTime: event.target.value })}
                                descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockSchedulePopup.input.startTime"
                              />

                              <InputField
                                type="time"
                                value={day.endTime ?? ""}
                                placeholder={t("schedule.end_time") + " - Ex: 23:00"}
                                onChange={(event) => updateDay(day.day, { endTime: event.target.value })}
                                descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockSchedulePopup.input.endTime"
                              />
                            </>
                          )}

                          <InputField
                            type="number"
                            value={String(day.expectedMinutes)}
                            min={0}
                            max={1440}
                            placeholder={t("schedule.expected_minutes") + " - Ex: 480"}
                            onChange={(event) => updateDay(day.day, { expectedMinutes: Number(event.target.value) })}
                            descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockSchedulePopup.input.expectedMinutes"
                          />

                          <div className={defaultStyle.divisionBar}></div>
                        </div>
                      )}
                    </div>
                  </Animate>
                ))}

                <div className={defaultStyle.divisionBar}></div>

                <Animate type={AnimationTypes.slideUpIn} duration="2.4s">
                  <div className={defaultStyle.buttonsController}>
                    <Button
                      typeButton="submit"
                      label={t("schedule.save")}
                      bgColor={THEME.colors.buttonBGSaveColor}
                      iconColor={THEME.colors.iconBGSecondaryColor}
                      disabled={isSaving}
                      descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockSchedulePopup.button.save"
                    />
                  </div>
                </Animate>
              </form>
            )}
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
